/**
 * The handbook's front page.
 *
 * Shelves rather than one long list, because the question a colleague arrives
 * with is almost never "show me everything" - it is "how do we do WordPress" or
 * "what happens when a site is down". Search flattens the shelves on purpose:
 * once somebody has typed, the grouping is in the way of the answer.
 *
 * Every card says how many picture slots are still empty. That number is the
 * handbook's own to-do list, and hiding it is how a guide stays half finished
 * for a year.
 */
import { useNavigate } from "react-router-dom";
import { BookOpen, Camera, Clock3, Plus, WifiOff } from "lucide-react";
import { cn, LoadingState } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { KB_CATEGORIES, type KbCard } from "../../../lib/kb";
import { Btn, Card, EmptyState, PageHead, SearchField, StatusPill, Toolbar, ToolbarGap } from "../../ui";
import { KB_CATEGORY_META } from "./meta";
import { groupByCategory, guideHref } from "./lib";
import { useGuideIndex } from "./useGuides";

function GuideCard({ card, onOpen }: { card: KbCard; onOpen: () => void }) {
  const t = useT();
  return (
    <button type="button" className="crm-kb-card" onClick={onOpen}>
      <div className="crm-kb-card-t">
        {card.title}
        {card.status === "draft" ? <StatusPill tone="wt">{t("kb.draft")}</StatusPill> : null}
      </div>
      <p className="crm-kb-card-s">{card.summary}</p>
      <div className="crm-kb-card-foot">
        <span title={t("kb.readTime")}>
          <Clock3 className="size-3.5" />
          {t("kb.minutes", { n: card.readMinutes })}
        </span>
        {card.audience ? <span className="crm-kb-card-aud">{card.audience}</span> : null}
        {card.openShots > 0 ? (
          <span className="crm-kb-card-shots" title={t("kb.shotsMissingHint")}>
            <Camera className="size-3.5" />
            {t("kb.shotsMissing", { n: card.openShots })}
          </span>
        ) : null}
      </div>
    </button>
  );
}

export function GuidesIndexPage({ user }: { user: StaffUser }) {
  const t = useT();
  const navigate = useNavigate();
  const canEdit = userCanPlatform(user, "manage:crm.kb");
  const desk = useGuideIndex({ includeDrafts: canEdit });

  const cards = desk.data?.articles ?? [];
  const counts = desk.data?.counts ?? {};
  const searching = Boolean(desk.q.trim());
  const groups = groupByCategory(cards, KB_CATEGORIES);

  return (
    <div className="crm-page">
      <PageHead
        title={t("kb.title")}
        sub={t("kb.sub")}
        actions={
          canEdit ? (
            <Btn tone="dark" onClick={() => navigate("/guides/uus")}>
              <Plus className="size-4" />
              {t("kb.new")}
            </Btn>
          ) : null
        }
      />

      <Toolbar>
        <SearchField value={desk.q} onChange={desk.setQ} placeholder={t("kb.searchPlaceholder")} />
        <ToolbarGap />
        <button
          type="button"
          className={cn("crm-cb crm-cb-wide crm-cb-sm", desk.category === null && "is-on")}
          onClick={() => desk.setCategory(null)}
        >
          {t("kb.allShelves")}
        </button>
        {KB_CATEGORIES.map((c) => {
          const Icon = KB_CATEGORY_META[c].icon;
          return (
            <button
              key={c}
              type="button"
              className={cn("crm-cb crm-cb-wide crm-cb-sm", desk.category === c && "is-on")}
              onClick={() => desk.setCategory(desk.category === c ? null : c)}
            >
              <Icon className="size-3.5" />
              {t(KB_CATEGORY_META[c].labelKey)}
              {counts[c] ? <span className="crm-cb-n">{counts[c]}</span> : null}
            </button>
          );
        })}
      </Toolbar>

      {desk.loading && !desk.data ? <Card className="crm-kb-loading"><LoadingState label={t("common.loading")} /></Card> : null}

      {/* A failed load must not read as an empty handbook. Without this the
          screen answers "there are no guides yet" to a request that never
          arrived, which is the one wrong answer it can give. */}
      {desk.error ? (
        <EmptyState
          icon={WifiOff}
          title={t("kb.errorTitle")}
          body={t("kb.errorBody")}
          action={<Btn onClick={desk.reload}>{t("kb.retry")}</Btn>}
        />
      ) : null}

      {desk.data && !desk.error && cards.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title={searching ? t("kb.emptySearchTitle") : t("kb.emptyTitle")}
          body={searching ? t("kb.emptySearchBody") : t("kb.emptyBody")}
        />
      ) : null}

      {desk.error ? null : searching ? (
        <div className="crm-kb-grid">
          {cards.map((card) => (
            <GuideCard key={card.id} card={card} onOpen={() => navigate(guideHref(card.slug))} />
          ))}
        </div>
      ) : (
        groups.map((group) => {
          const Icon = KB_CATEGORY_META[group.category as keyof typeof KB_CATEGORY_META].icon;
          return (
            <section key={group.category} className="crm-kb-shelf">
              <h2>
                <Icon className="size-4" strokeWidth={2} />
                {t(KB_CATEGORY_META[group.category as keyof typeof KB_CATEGORY_META].labelKey)}
                <span>{group.cards.length}</span>
              </h2>
              <div className="crm-kb-grid">
                {group.cards.map((card) => (
                  <GuideCard key={card.id} card={card} onOpen={() => navigate(guideHref(card.slug))} />
                ))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
